/**
 * Audio Manager - Handles all audio playback using Howler.js
 *
 * Central audio: bubble, choice, custom/scene sounds; fade in/out; speech volume.
 * Features: bubble sounds (fade out on rapid clicks), choice sounds; future: background music.
 */

import { Howl, Howler } from 'howler'
import { AudioType, AudioConfig, PlayingSound } from './types'
import { AUDIO_BASE_PATH, AUDIO_CONFIG, getVolumeCeilings } from './constants'
import { assetUrl } from '../../utils/pathResolver'
import { unlockAudioContext } from './unlock-audio-context'

export interface AudioPatternsConfig {
  chapterTitle: string
  bubble: string
  choice: string
}

const DEFAULT_PATTERNS: AudioPatternsConfig = {
  chapterTitle: 'text.mp3',
  bubble: 'bubble.mp3',
  choice: 'choice.mp3',
}

export class AudioManager {
  private config: AudioConfig
  private patterns: AudioPatternsConfig
  private speechVolume: number = 1.0
  private soundVolume: number = 0.7
  private currentBubbleSound: PlayingSound | null = null
  private currentChoiceSound: PlayingSound | null = null
  private currentCustomSound: { key: string; howl: Howl; soundId: number; volumeCategory: 'speech' | 'sound' } | null = null
  private sounds: Map<string, Howl> = new Map()
  private soundUrls: Map<Howl, string> = new Map()
  private missingFiles: Set<string> = new Set()

  constructor(config: Partial<AudioConfig> = {}, patterns?: Partial<AudioPatternsConfig>) {
    this.config = {
      volume: config.volume ?? AUDIO_CONFIG.volume,
      fadeOutDuration: config.fadeOutDuration ?? AUDIO_CONFIG.fadeOutDuration,
      fadeInDuration: config.fadeInDuration ?? AUDIO_CONFIG.fadeInDuration,
    }
    this.patterns = { ...DEFAULT_PATTERNS, ...patterns }

    Howler.volume(1.0)
  }

  /** Derive an indexed filename from a pattern, e.g. "bubble.mp3" + 3 → "bubble-3.mp3" */
  resolveIndexedPattern(pattern: string, index: number | string): string {
    const dot = pattern.lastIndexOf('.')
    if (dot === -1) return `${pattern}-${index}`
    return `${pattern.slice(0, dot)}-${index}${pattern.slice(dot)}`
  }

  /** Get the configured chapter-title sound filename (e.g. "text.mp3") */
  getChapterTitleSound(): string { return this.patterns.chapterTitle }

  /** Get the configured bubble sound pattern (e.g. "bubble.mp3") */
  getBubblePattern(): string { return this.patterns.bubble }

  /** Get the configured choice sound pattern (e.g. "choice.mp3") */
  getChoicePattern(): string { return this.patterns.choice }

  /**
   * Load a Howl using a low-priority fetch first so the download doesn't
   * compete with critical image loads. Once the file is in the HTTP cache
   * Howler picks it up almost instantly.
   */
  private loadSoundLowPriority(sound: Howl): void {
    if (sound.state() !== 'unloaded') return
    const url = this.soundUrls.get(sound)
    if (!url) { sound.load(); return }
    fetch(url, { priority: 'low' } as RequestInit)
      .catch(() => {})
      .finally(() => { if (sound.state() === 'unloaded') sound.load() })
  }

  /**
   * Attach a one-shot playerror handler that resumes the AudioContext and
   * retries playback once. On iPadOS the first play() after page load is
   * often rejected even though ctx.state appears "running".
   */
  private attachPlayRetry(sound: Howl, play: () => void): void {
    sound.once('playerror', () => {
      unlockAudioContext()
      const ctx = Howler.ctx as AudioContext | undefined
      if (ctx && ctx.state === 'suspended') {
        ctx.resume().then(() => { sound.once('unlock', play) })
      } else {
        sound.once('unlock', play)
      }
    })
  }

  /**
   * Get or create a Howl instance for a sound file
   */
  private getSound(type: AudioType, index: number, sceneId?: string): Howl {
    const soundKey = sceneId ? `${type}-${index}-${sceneId}` : `${type}-${index}`
    
    if (this.sounds.has(soundKey)) {
      return this.sounds.get(soundKey)!
    }

    const pattern = type === 'bubble' ? this.patterns.bubble : this.patterns.choice
    const fileName = this.resolveIndexedPattern(pattern, index)
    const soundPath = sceneId 
      ? assetUrl(`/assets/scenes/${sceneId}/${fileName}`)
      : `${AUDIO_BASE_PATH}/${fileName}`
    
    const howl = new Howl({
      src: [soundPath],
      volume: this.speechVolume,
      preload: false,
      html5: false,
    })

    this.sounds.set(soundKey, howl)
    this.soundUrls.set(howl, soundPath)
    return howl
  }

  /**
   * Get or create a Howl instance for a custom sound file (e.g., button.mp3, text.mp3)
   */
  private getCustomSound(fileName: string, sceneId: string): Howl {
    const soundKey = `custom-${fileName}-${sceneId}`
    
    if (this.sounds.has(soundKey)) {
      return this.sounds.get(soundKey)!
    }

    // Use scene-specific path for custom sounds
    const soundPath = assetUrl(`/assets/scenes/${sceneId}/${fileName}`)
    
    const howl = new Howl({
      src: [soundPath],
      volume: this.soundVolume,
      preload: false,
      html5: false,
    })

    this.sounds.set(soundKey, howl)
    this.soundUrls.set(howl, soundPath)
    return howl
  }

  /**
   * Get or create a Howl instance for a sound file with custom name (for minigame items)
   */
  private getSoundByName(type: AudioType, name: string, sceneId: string): Howl {
    const soundKey = `${type}-${name}-${sceneId}`
    
    if (this.sounds.has(soundKey)) {
      return this.sounds.get(soundKey)!
    }

    const pattern = type === 'bubble' ? this.patterns.bubble : this.patterns.choice
    const fileName = this.resolveIndexedPattern(pattern, name)
    const soundPath = assetUrl(`/assets/scenes/${sceneId}/items/${fileName}`)
    
    const howl = new Howl({
      src: [soundPath],
      volume: this.speechVolume,
      preload: false,
      html5: false,
    })

    this.sounds.set(soundKey, howl)
    this.soundUrls.set(howl, soundPath)
    return howl
  }

  /**
   * Fade out and stop a currently playing sound
   */
  private fadeOutSound(sound: Howl, soundId: number): void {
    if (!sound.playing(soundId)) return

    const currentVolume = sound.volume(soundId) as number
    sound.fade(currentVolume, 0, this.config.fadeOutDuration, soundId)
    
    setTimeout(() => {
      sound.stop(soundId)
    }, this.config.fadeOutDuration)
  }

  private fadeOutCurrentBubble(): void {
    if (!this.currentBubbleSound) return
    let currentSound: Howl | null = null
    if (this.currentBubbleSound.index === -1 && this.currentBubbleSound.itemName && this.currentBubbleSound.sceneId) {
      currentSound = this.getSoundByName('bubble', this.currentBubbleSound.itemName, this.currentBubbleSound.sceneId)
    } else if (this.currentBubbleSound.index !== -1) {
      currentSound = this.getSound(this.currentBubbleSound.type, this.currentBubbleSound.index, this.currentBubbleSound.sceneId)
    }
    if (currentSound && currentSound.playing(this.currentBubbleSound.soundId)) {
      this.fadeOutSound(currentSound, this.currentBubbleSound.soundId)
    }
    this.currentBubbleSound = null
  }

  private fadeOutCurrentChoice(): void {
    if (!this.currentChoiceSound) return
    const currentSound = this.getSound(this.currentChoiceSound.type, this.currentChoiceSound.index, this.currentChoiceSound.sceneId)
    if (currentSound.playing(this.currentChoiceSound.soundId)) {
      this.fadeOutSound(currentSound, this.currentChoiceSound.soundId)
    }
    this.currentChoiceSound = null
  }

  private fadeOutCurrentCustom(): void {
    if (!this.currentCustomSound) return
    if (this.currentCustomSound.howl.playing(this.currentCustomSound.soundId)) {
      this.fadeOutSound(this.currentCustomSound.howl, this.currentCustomSound.soundId)
    }
    this.currentCustomSound = null
  }

  private fadeOutCurrentCustomIfSpeech(): void {
    if (!this.currentCustomSound || this.currentCustomSound.volumeCategory !== 'speech') return
    this.fadeOutCurrentCustom()
  }

  /**
   * Play a bubble sound by name (for minigame items)
   */
  playBubbleSoundByName(itemName: string, sceneId: string): void {
    this.fadeOutCurrentBubble()
    this.fadeOutCurrentChoice()
    this.fadeOutCurrentCustomIfSpeech()

    const sound = this.getSoundByName('bubble', itemName, sceneId)
    const bubbleFileName = this.resolveIndexedPattern(this.patterns.bubble, itemName)
    
    sound.once('loaderror', (id, error) => {
      console.error(`[AudioManager] Failed to load ${bubbleFileName}:`, error)
      console.error(`[AudioManager] Make sure the file exists at: /assets/scenes/${sceneId}/${bubbleFileName}`)
    })

    const doPlay = () => this.playSoundWithFadeInByName(sound, itemName, sceneId)
    this.attachPlayRetry(sound, doPlay)

    if (sound.state() === 'loaded') {
      doPlay()
      return
    }

    if (sound.state() === 'unloaded') {
      this.loadSoundLowPriority(sound)
    }

    sound.once('load', () => {
      doPlay()
    })
  }

  /**
   * Helper method to play a sound with fade in by name
   */
  private playSoundWithFadeInByName(sound: Howl, itemName: string, sceneId: string): void {
    const soundId = sound.play()
    
    if (soundId === undefined) {
      console.error(`[AudioManager] Failed to play ${this.resolveIndexedPattern(this.patterns.bubble, itemName)} - sound may not be loaded`)
      return
    }
    
    sound.volume(0, soundId)
    sound.fade(0, this.speechVolume, this.config.fadeInDuration, soundId)

    // Store as current bubble sound (using a special index to indicate it's a named sound)
    this.currentBubbleSound = {
      soundId,
      type: 'bubble',
      index: -1, // Special value to indicate named sound
      sceneId,
      itemName, // Store item name for retrieval
    }

    // Clean up when sound finishes
    sound.once('end', () => {
      if (this.currentBubbleSound?.soundId === soundId) {
        this.currentBubbleSound = null
      }
    })
  }

  /**
   * Play a bubble sound with fade out support for rapid clicks.
   * If the same bubble sound is already playing, does nothing (no overlap).
   */
  playBubbleSound(bubbleIndex: number, sceneId?: string): void {
    if (this.currentBubbleSound && this.currentBubbleSound.index === bubbleIndex && this.currentBubbleSound.sceneId === sceneId) {
      const currentSound = this.getSound('bubble', bubbleIndex, sceneId)
      if (currentSound.playing(this.currentBubbleSound.soundId)) return
    }

    this.fadeOutCurrentBubble()
    this.fadeOutCurrentChoice()
    this.fadeOutCurrentCustomIfSpeech()

    const sound = this.getSound('bubble', bubbleIndex, sceneId)
    const bubbleFileName = this.resolveIndexedPattern(this.patterns.bubble, bubbleIndex)

    sound.once('loaderror', (id, error) => {
      console.error(`[AudioManager] Failed to load ${bubbleFileName}:`, error)
      console.error(`[AudioManager] Make sure the file exists at: /assets/scenes/${sceneId ?? 'audio'}/${bubbleFileName}`)
    })

    const doPlay = () => this.playSoundWithFadeIn(sound, 'bubble', bubbleIndex, sceneId)
    this.attachPlayRetry(sound, doPlay)

    if (sound.state() === 'loaded') {
      doPlay()
      return
    }

    if (sound.state() === 'unloaded') {
      this.loadSoundLowPriority(sound)
    }

    sound.once('load', () => {
      doPlay()
    })
  }

  /**
   * Helper method to play a sound with fade in
   */
  private playSoundWithFadeIn(sound: Howl, type: 'bubble' | 'choice', index: number, sceneId?: string): void {
    const soundId = sound.play()
    
    if (soundId === undefined) {
      const pattern = type === 'bubble' ? this.patterns.bubble : this.patterns.choice
      console.error(`[AudioManager] Failed to play ${this.resolveIndexedPattern(pattern, index)} - sound may not be loaded`)
      return
    }
    
    sound.volume(0, soundId)
    sound.fade(0, this.speechVolume, this.config.fadeInDuration, soundId)

    if (type === 'bubble') {
      this.currentBubbleSound = {
        soundId,
        type: 'bubble',
        index,
        sceneId,
      }

      // Clean up when sound finishes
      sound.once('end', () => {
        if (this.currentBubbleSound?.soundId === soundId) {
          this.currentBubbleSound = null
        }
      })
    } else {
      this.currentChoiceSound = {
        soundId,
        type: 'choice',
        index,
        sceneId,
      }

      // Clean up when sound finishes
      sound.once('end', () => {
        if (this.currentChoiceSound?.soundId === soundId) {
          this.currentChoiceSound = null
        }
      })
    }
  }

  /**
   * Play a choice sound
   */
  playChoiceSound(choiceIndex: number, sceneId?: string): void {
    this.fadeOutCurrentBubble()
    this.fadeOutCurrentChoice()
    this.fadeOutCurrentCustomIfSpeech()

    const sound = this.getSound('choice', choiceIndex, sceneId)
    
    // Handle errors
    const choiceFileName = this.resolveIndexedPattern(this.patterns.choice, choiceIndex)
    sound.once('loaderror', (id, error) => {
      const expectedPath = sceneId 
        ? assetUrl(`/assets/scenes/${sceneId}/${choiceFileName}`)
        : assetUrl(`/assets/audio/${choiceFileName}`)
      console.error(`[AudioManager] Failed to load ${choiceFileName}:`, error)
      console.error(`[AudioManager] Make sure the file exists at: ${expectedPath}`)
    })

    const doPlay = () => this.playSoundWithFadeIn(sound, 'choice', choiceIndex, sceneId)
    this.attachPlayRetry(sound, doPlay)

    if (sound.state() === 'loaded') {
      doPlay()
      return
    }

    if (sound.state() === 'unloaded') {
      this.loadSoundLowPriority(sound)
    }

    sound.once('load', () => {
      doPlay()
    })
  }

  /**
   * Set global volume (0.0 to 1.0) — kept for backward compat, sets sound volume
   */
  setVolume(volume: number): void {
    this.soundVolume = Math.max(0, Math.min(1, volume))
  }

  /**
   * Get current sound volume (0.0 to 1.0)
   */
  getVolume(): number {
    return this.soundVolume
  }

  /**
   * Set speech volume (Řeč: bubbles, choices, chapter-title text)
   * Volume is 0-100 (user slider), scaled by the speech ceiling from VOLUME_CEILINGS.
   */
  setSpeechVolume(volume: number): void {
    const ceiling = getVolumeCeilings().speech
    this.speechVolume = (Math.max(0, Math.min(100, volume)) / 100) * ceiling

    if (this.currentBubbleSound) {
      let currentSound: Howl | null = null
      if (this.currentBubbleSound.index === -1 && this.currentBubbleSound.itemName && this.currentBubbleSound.sceneId) {
        currentSound = this.getSoundByName('bubble', this.currentBubbleSound.itemName, this.currentBubbleSound.sceneId)
      } else if (this.currentBubbleSound.index !== -1) {
        currentSound = this.getSound(
          this.currentBubbleSound.type,
          this.currentBubbleSound.index,
          this.currentBubbleSound.sceneId
        )
      }
      if (currentSound) {
        currentSound.volume(this.speechVolume, this.currentBubbleSound.soundId)
      }
    }
    
    if (this.currentChoiceSound) {
      const currentSound = this.getSound(
        this.currentChoiceSound.type,
        this.currentChoiceSound.index,
        this.currentChoiceSound.sceneId
      )
      currentSound.volume(this.speechVolume, this.currentChoiceSound.soundId)
    }

    if (this.currentCustomSound?.volumeCategory === 'speech') {
      this.currentCustomSound.howl.volume(this.speechVolume, this.currentCustomSound.soundId)
    }
  }

  /**
   * Set sound effects volume (Zvuky: custom sounds, global sounds, scroll-tied, etc.)
   * Volume is 0-100 (user slider), scaled by the sound ceiling from VOLUME_CEILINGS.
   */
  setSoundVolume(volume: number): void {
    const ceiling = getVolumeCeilings().sound
    this.soundVolume = (Math.max(0, Math.min(100, volume)) / 100) * ceiling

    if (this.currentCustomSound?.volumeCategory === 'sound') {
      this.currentCustomSound.howl.volume(this.soundVolume, this.currentCustomSound.soundId)
    }
  }

  /**
   * Get speech volume (0-100)
   */
  getSpeechVolume(): number {
    return Math.round(this.speechVolume * 100)
  }

  /**
   * Get sound effects volume (0-100)
   */
  getSoundVolume(): number {
    return Math.round(this.soundVolume * 100)
  }

  /**
   * Play a custom sound file (e.g., button.mp3, text.mp3) from a scene folder.
   * If the same sound is already playing, does nothing (avoids overlap when scrolling quickly).
   * When a different custom sound starts, the previous one fades out.
   */
  playCustomSound(fileName: string, sceneId: string, volumeCategory: 'speech' | 'sound' = 'sound'): void {
    const key = `custom:${sceneId}/${fileName}`
    const sound = this.getCustomSound(fileName, sceneId)

    if (sound.playing()) return

    if (volumeCategory === 'speech') {
      this.fadeOutCurrentBubble()
      this.fadeOutCurrentChoice()
    }
    this.fadeOutCurrentCustom()

    sound.once('loaderror', (id, error) => {
      console.error(`[AudioManager] Failed to load ${fileName}:`, error)
      console.error(`[AudioManager] Make sure the file exists at: /assets/scenes/${sceneId}/${fileName}`)
    })

    const vol = volumeCategory === 'speech' ? this.speechVolume : this.soundVolume
    const trackAndPlay = () => {
      const soundId = sound.play() as unknown as number
      sound.volume(vol, soundId)
      this.currentCustomSound = { key, howl: sound, soundId, volumeCategory }
      sound.once('end', () => {
        if (this.currentCustomSound?.soundId === soundId) {
          this.currentCustomSound = null
        }
      })
    }

    this.attachPlayRetry(sound, trackAndPlay)

    if (sound.state() === 'loaded') {
      trackAndPlay()
      return
    }

    if (sound.state() === 'unloaded') {
      this.loadSoundLowPriority(sound)
    }

    sound.once('load', () => {
      trackAndPlay()
    })
  }


  /**
   * Get the duration (in ms) of a custom sound. Returns 0 if not loaded yet.
   */
  getCustomSoundDuration(fileName: string, sceneId: string): number {
    const soundKey = `custom-${fileName}-${sceneId}`
    const sound = this.sounds.get(soundKey)
    if (!sound || sound.state() !== 'loaded') return 0
    return sound.duration() * 1000
  }

  /**
   * Like playCustomSound but silently skips if the file does not exist.
   * Uses a HEAD request to probe the file before loading it through Howler.
   */
  async tryPlayCustomSound(fileName: string, sceneId: string, volumeCategory: 'speech' | 'sound' = 'sound'): Promise<number> {
    const cacheKey = `${sceneId}/${fileName}`
    if (this.missingFiles.has(cacheKey)) return 0

    const soundKey = `custom-${fileName}-${sceneId}`
    if (this.sounds.has(soundKey)) {
      this.playCustomSound(fileName, sceneId, volumeCategory)
      return this.getCustomSoundDuration(fileName, sceneId)
    }

    const soundPath = assetUrl(`/assets/scenes/${sceneId}/${fileName}`)
    try {
      const res = await fetch(soundPath, { method: 'HEAD' })
      if (!res.ok) {
        this.missingFiles.add(cacheKey)
        return 0
      }
    } catch {
      this.missingFiles.add(cacheKey)
      return 0
    }
    this.playCustomSound(fileName, sceneId, volumeCategory)

    // Wait for the sound to load so we can return its duration
    const sound = this.sounds.get(soundKey)
    if (sound && sound.state() !== 'loaded') {
      await new Promise<void>((resolve) => {
        sound.once('load', () => resolve())
        sound.once('loaderror', () => resolve())
      })
    }
    return this.getCustomSoundDuration(fileName, sceneId)
  }

  /**
   * Play a global sound file from /assets/audio/ (e.g., intro.mp3).
   * Uses the same overlap prevention as custom sounds — fades out the previous one.
   */
  playGlobalSound(fileName: string): void {
    const key = `global:${fileName}`
    const soundKey = `global-${fileName}`

    let sound = this.sounds.get(soundKey)
    if (!sound) {
      const url = assetUrl(`/assets/audio/${fileName}`)
      sound = new Howl({
        src: [url],
        volume: this.speechVolume,
        preload: false,
        html5: false,
      })
      this.sounds.set(soundKey, sound)
      this.soundUrls.set(sound, url)
    }

    if (sound.playing()) return

    this.fadeOutCurrentBubble()
    this.fadeOutCurrentChoice()
    this.fadeOutCurrentCustomIfSpeech()

    sound.once('loaderror', (_id, error) => {
      console.error(`[AudioManager] Failed to load global sound ${fileName}:`, error)
    })

    const trackAndPlay = () => {
      const soundId = sound!.play() as unknown as number
      sound!.volume(this.speechVolume, soundId)
      this.currentCustomSound = { key, howl: sound!, soundId, volumeCategory: 'speech' }
      sound!.once('end', () => {
        if (this.currentCustomSound?.soundId === soundId) {
          this.currentCustomSound = null
        }
      })
    }

    this.attachPlayRetry(sound, trackAndPlay)

    if (sound.state() === 'loaded') {
      trackAndPlay()
      return
    }

    if (sound.state() === 'unloaded') {
      this.loadSoundLowPriority(sound)
    }

    sound.once('load', () => {
      trackAndPlay()
    })
  }

  /**
   * Fade out speech and custom sounds only (bubble, choice, custom).
   * Does NOT stop music/atmo managed by AtmoManager.
   */
  fadeOutSpeechAndCustom(): void {
    this.fadeOutCurrentBubble()
    this.fadeOutCurrentChoice()
    this.fadeOutCurrentCustom()
  }

  /**
   * Fade out all currently playing sounds (bubble, choice, custom) then hard-stop everything.
   * Use when navigating to home screen (kills music/atmo too via Howler.stop).
   */
  fadeOutAll(): void {
    this.fadeOutCurrentBubble()
    this.fadeOutCurrentChoice()
    this.fadeOutCurrentCustom()
    setTimeout(() => {
      Howler.stop()
    }, this.config.fadeOutDuration)
  }

  /**
   * Stop all sounds immediately (no fade)
   */
  stopAll(): void {
    Howler.stop()
    this.currentBubbleSound = null
    this.currentChoiceSound = null
    this.currentCustomSound = null
  }

  /**
   * Clean up resources
   */
  destroy(): void {
    this.stopAll()
    this.sounds.forEach((sound) => {
      sound.unload()
    })
    this.sounds.clear()
    this.soundUrls.clear()
  }
}

// Singleton instance
let audioManagerInstance: AudioManager | null = null

/**
 * Get the global AudioManager instance
 */
export function getAudioManager(): AudioManager {
  if (!audioManagerInstance) {
    audioManagerInstance = new AudioManager()
  }
  return audioManagerInstance
}

/**
 * Initialize audio manager (can be called with custom config)
 */
export function initAudioManager(config?: Partial<AudioConfig>, patterns?: Partial<AudioPatternsConfig>): AudioManager {
  if (audioManagerInstance) {
    audioManagerInstance.destroy()
  }
  audioManagerInstance = new AudioManager(config, patterns)
  return audioManagerInstance
}
